import React, { useEffect, useState } from 'react';
import { useNavigate }                from 'react-router-dom';
import { useAuth }                    from '../contexts/AuthContext';
import { getCart, removeFromCart, pickSupplier, purchaseCartItem } from '../api/parts';

const PRIORITY_COLORS = {
  HIGH:   '#7a2a2a',
  NORMAL: '#2a4a7a',
  LOW:    '#3a5a3a',
};

export default function CartPage() {
  const { user, signOut } = useAuth();
  const navigate          = useNavigate();

  const [vehicle,        setVehicle]        = useState(null);
  const [items,          setItems]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');

  /* per-item purchase price input state */
  const [purchasePrices, setPurchasePrices] = useState({});
  const [busy,           setBusy]           = useState({});

  useEffect(() => {
    const cached = localStorage.getItem('ar_active_vehicle');
    if (cached) {
      try {
        const v = JSON.parse(cached);
        setVehicle(v);
        fetchCart(v.vehicleId);
      } catch (_) {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchCart(vehicleId) {
    setLoading(true);
    setError('');
    try {
      const res = await getCart(vehicleId);
      if (res.success) {
        setItems(res.data || []);
      } else {
        setError(res.message || 'Could not load cart.');
      }
    } catch {
      setError('Could not load cart.');
    } finally {
      setLoading(false);
    }
  }

  function setBusyItem(id, val) {
    setBusy(prev => ({ ...prev, [id]: val }));
  }

  async function handleRemove(item) {
    if (!window.confirm(`Remove "${item.part_name}" from cart?`)) return;
    setBusyItem(item.shoppingListId, true);
    try {
      await removeFromCart(vehicle.vehicleId, item.shoppingListId);
      setItems(prev => prev.filter(i => i.shoppingListId !== item.shoppingListId));
    } catch {
      alert('Could not remove item. Please try again.');
    } finally {
      setBusyItem(item.shoppingListId, false);
    }
  }

  async function handlePickSupplier(item, partSupplierId) {
    if (!partSupplierId) return;
    const suppliers = parseSuppliers(item.available_suppliers);
    const chosen    = suppliers.find(s => String(s.partSupplierId) === String(partSupplierId));
    if (!chosen) return;

    setBusyItem(item.shoppingListId, true);
    try {
      await pickSupplier(vehicle.vehicleId, item.shoppingListId, {
        supplierId:    chosen.supplierId,
        partSupplierId: chosen.partSupplierId,
      });
      // Refresh cart to get updated selected supplier info
      await fetchCart(vehicle.vehicleId);
    } catch {
      alert('Could not select supplier. Please try again.');
    } finally {
      setBusyItem(item.shoppingListId, false);
    }
  }

  async function handlePurchase(item) {
    const price = purchasePrices[item.shoppingListId] ?? '';
    const paid  = price !== '' ? parseFloat(price) : null;

    if (!window.confirm(`Mark "${item.part_name}" as purchased${paid ? ` for $${paid.toFixed(2)}` : ''}?`)) return;
    setBusyItem(item.shoppingListId, true);
    try {
      await purchaseCartItem(vehicle.vehicleId, item.shoppingListId, { purchased_price: paid });
      setItems(prev => prev.filter(i => i.shoppingListId !== item.shoppingListId));
    } catch {
      alert('Could not mark as purchased. Please try again.');
    } finally {
      setBusyItem(item.shoppingListId, false);
    }
  }

  function parseSuppliers(raw) {
    if (!raw) return [];
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return [];
    }
  }

  const vehicleLabel = vehicle
    ? `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim_package ? ' ' + vehicle.trim_package : ''}`.trim()
    : '—';

  return (
    <div className="pts-root">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <header className="pts-header">
        <div className="pts-header-brand">
          <span className="pts-header-gear">⚙</span>
          <span className="pts-header-name">AutoRestoration</span>
        </div>

        <div className="pts-header-vehicle">
          {vehicle && (
            <>
              <span className="pts-vehicle-icon">🚗</span>
              <span className="pts-vehicle-label">{vehicleLabel}</span>
              {vehicle.nickname && (
                <span className="pts-vehicle-nick">"{vehicle.nickname}"</span>
              )}
            </>
          )}
        </div>

        <div className="pts-header-user">
          <span className="pts-username">@{user?.username}</span>
          <button className="pts-signout-btn" onClick={() => { signOut(); navigate('/'); }}>Sign Out</button>
        </div>
      </header>

      {/* ── Page body ───────────────────────────────────────────────── */}
      <div className="pts-body">

        <button className="pts-back-dash" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>

        <div className="pts-page-title-row">
          <div className="pts-breadcrumb">
            <span className="pts-crumb pts-crumb-active">🛒 Cart</span>
          </div>
        </div>

        {loading && <div className="pts-loading">Loading cart…</div>}

        {!loading && error && <div className="pts-error">{error}</div>}

        {!loading && !error && items.length === 0 && (
          <div className="pts-empty">
            <div className="pts-empty-icon">🛒</div>
            <h3 className="pts-empty-title">Cart is empty</h3>
            <p className="pts-empty-text">
              Open a part and use the <strong>Add to Cart</strong> button to queue
              parts for purchase. They will appear here.
            </p>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="pts-parts-section">
            {/* ── Cart total summary ─────────────────────────────────── */}
            {(() => {
              const priced   = items.filter(i => i.selected_listing_price || i.target_price);
              const unpriced = items.length - priced.length;
              const total    = priced.reduce((sum, i) => {
                const p = parseFloat(i.selected_listing_price ?? i.target_price ?? 0);
                return sum + (isNaN(p) ? 0 : p);
              }, 0);
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: 8,
                  background: '#1a2236', border: '1px solid #2e3a4a', borderRadius: 8,
                  padding: '10px 16px', marginBottom: 12,
                }}>
                  <span style={{ color: '#aaa', fontSize: '0.9rem' }}>
                    {items.length} item{items.length !== 1 ? 's' : ''} to purchase
                  </span>
                  <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ color: '#8899aa', fontSize: '0.82rem' }}>
                      {unpriced > 0
                        ? `Estimated total (${unpriced} item${unpriced !== 1 ? 's' : ''} no price):`
                        : 'Total:'}
                    </span>
                    <span style={{ color: '#4d9de0', fontSize: '1.2rem', fontWeight: 700 }}>
                      ${total.toFixed(2)}
                    </span>
                  </span>
                </div>
              );
            })()}

            <div className="pts-parts-header-row">
              <span className="pts-parts-count">
                {items.length} item{items.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="pts-parts-list">
              {items.map(item => {
                const suppliers     = parseSuppliers(item.available_suppliers);
                const isBusy        = !!busy[item.shoppingListId];
                const currentPsId   = item.partSupplierId ? String(item.partSupplierId) : '';
                const priceInput    = purchasePrices[item.shoppingListId] ?? '';

                return (
                  <div key={item.shoppingListId} className="pts-part-card">

                    {/* ── Part info ─────────────────────────────────── */}
                    <div className="pts-part-main">
                      <span
                        className="pts-part-name pts-part-card-clickable"
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/parts/${item.partId}`, { state: { from: 'cart' } })}
                      >
                        {item.part_name}
                      </span>
                      {item.oem_part_number && (
                        <span className="pts-part-oem">OEM# {item.oem_part_number}</span>
                      )}
                    </div>

                    <div className="pts-part-meta" style={{ marginBottom: 8 }}>
                      <span className="pts-part-condition" style={{ background: '#2a4a7a' }}>
                        {item.category_name}
                      </span>
                      <span
                        className="pts-part-condition"
                        style={{ background: PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.NORMAL }}
                      >
                        {item.priority}
                      </span>
                      {item.target_price && (
                        <span className="pts-part-flag">Target: ${parseFloat(item.target_price).toFixed(2)}</span>
                      )}
                    </div>

                    {/* ── Selected supplier ─────────────────────────── */}
                    {item.selected_supplier_name ? (
                      <div style={{ fontSize: '0.82rem', color: '#aaa', marginBottom: 6 }}>
                        <strong>Supplier:</strong> {item.selected_supplier_name}
                        {item.selected_listing_price && (
                          <> — <strong>${parseFloat(item.selected_listing_price).toFixed(2)}</strong></>
                        )}
                        {item.selected_listing_url && (
                          <> · <a href={item.selected_listing_url} target="_blank" rel="noreferrer" style={{ color: '#6af' }}>View Listing</a></>
                        )}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.82rem', color: '#888', marginBottom: 6 }}>
                        No supplier selected yet.
                      </div>
                    )}

                    {/* ── Supplier picker ───────────────────────────── */}
                    {suppliers.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <select
                          style={{
                            background: '#1e2839', color: '#ccc', border: '1px solid #3a4a5a',
                            borderRadius: 4, padding: '4px 8px', fontSize: '0.82rem', width: '100%',
                          }}
                          value={currentPsId}
                          disabled={isBusy}
                          onChange={e => handlePickSupplier(item, e.target.value)}
                        >
                          <option value="">— Pick Supplier —</option>
                          {suppliers.map(s => (
                            <option key={s.partSupplierId} value={String(s.partSupplierId)}>
                              {s.supplier_name}
                              {s.listing_price ? ` — $${parseFloat(s.listing_price).toFixed(2)}` : ''}
                              {s.condition_code ? ` (${s.condition_code})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* ── Purchase price input + actions ────────────── */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Price paid ($)"
                        value={priceInput}
                        disabled={isBusy}
                        style={{
                          background: '#1e2839', color: '#ccc', border: '1px solid #3a4a5a',
                          borderRadius: 4, padding: '4px 8px', fontSize: '0.82rem', width: 130,
                        }}
                        onChange={e => setPurchasePrices(prev => ({ ...prev, [item.shoppingListId]: e.target.value }))}
                      />
                      <button
                        className="pts-modal-confirm"
                        style={{ padding: '4px 12px', fontSize: '0.82rem' }}
                        disabled={isBusy}
                        onClick={() => handlePurchase(item)}
                      >
                        {isBusy ? 'Working…' : '✓ Purchased'}
                      </button>
                      <button
                        className="pts-assign-cancel-btn"
                        style={{ padding: '4px 10px', fontSize: '0.82rem' }}
                        disabled={isBusy}
                        onClick={() => handleRemove(item)}
                      >
                        🗑 Remove
                      </button>
                    </div>

                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
